import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Input, Form, Modal, Select, message, Tag, Row, Col, InputNumber, Card, Statistic, DatePicker, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, DollarOutlined, EyeOutlined, WarningOutlined } from '@ant-design/icons';
import { receivableAPI, customerAPI } from '../../services/api';
import dayjs from 'dayjs';

const { Search } = Input;
const { Option } = Select;
const { TextArea } = Input;

const ReceivableList = () => {
  const [receivables, setReceivables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState({ status: '', customerId: '' });
  const [modalVisible, setModalVisible] = useState(false);
  const [receiveModalVisible, setReceiveModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [editingReceivable, setEditingReceivable] = useState(null);
  const [currentReceivable, setCurrentReceivable] = useState(null);
  const [receivableDetail, setReceivableDetail] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState({});
  const [form] = Form.useForm();
  const [receiveForm] = Form.useForm();

  const statusMap = {
    'pending': { text: '待收款', color: 'orange' },
    'partial': { text: '部分收款', color: 'blue' },
    'completed': { text: '已完成', color: 'green' },
    'overdue': { text: '已逾期', color: 'red' },
    'cancelled': { text: '已取消', color: 'default' }
  };

  const paymentMethodMap = {
    'cash': '现金',
    'bank': '银行转账',
    'wechat': '微信',
    'alipay': '支付宝',
    'check': '支票',
    'other': '其他'
  };

  const columns = [
    {
      title: '应收单号',
      dataIndex: 'billNo',
      key: 'billNo',
      width: 140
    },
    {
      title: '客户',
      dataIndex: ['customer', 'name'],
      key: 'customerName',
      width: 150,
      render: (text, record) => record.customer?.shortName || text
    },
    {
      title: '关联单号',
      dataIndex: 'relatedOrderNo',
      key: 'relatedOrderNo',
      width: 140,
      render: (text) => text || '-'
    },
    {
      title: '应收金额',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 120,
      render: (text) => <span style={{ color: '#1890ff', fontWeight: 'bold' }}>¥{parseFloat(text).toFixed(2)}</span>
    },
    {
      title: '已收金额',
      dataIndex: 'receivedAmount',
      key: 'receivedAmount',
      width: 120,
      render: (text) => <span style={{ color: '#52c41a' }}>¥{parseFloat(text).toFixed(2)}</span>
    },
    {
      title: '待收金额',
      dataIndex: 'pendingAmount',
      key: 'pendingAmount',
      width: 120,
      render: (text) => {
        const amount = parseFloat(text);
        return <span style={{ color: amount > 0 ? '#ff4d4f' : '#52c41a' }}>¥{amount.toFixed(2)}</span>;
      }
    },
    {
      title: '账单日期',
      dataIndex: 'billDate',
      key: 'billDate',
      width: 110,
      render: (text) => text ? dayjs(text).format('YYYY-MM-DD') : '-'
    },
    {
      title: '到期日期',
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 110,
      render: (text, record) => {
        if (!text) return '-';
        const isOverdue = record.status !== 'completed' && dayjs(text).isBefore(dayjs(), 'day');
        return (
          <span style={{ color: isOverdue ? '#ff4d4f' : undefined }}>
            {dayjs(text).format('YYYY-MM-DD')}
            {isOverdue && <WarningOutlined style={{ marginLeft: 4 }} />}
          </span>
        );
      }
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (text) => {
        const status = statusMap[text] || { text, color: 'default' };
        return <Tag color={status.color}>{status.text}</Tag>;
      }
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button type="link" icon={<EyeOutlined />} onClick={() => handleViewDetail(record)}>
            详情
          </Button>
          {record.status !== 'completed' && record.status !== 'cancelled' && (
            <Button type="link" icon={<DollarOutlined />} onClick={() => handleReceive(record)}>
              收款
            </Button>
          )}
          {record.status === 'pending' && parseFloat(record.receivedAmount) === 0 && (
            <>
              <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
                编辑
              </Button>
              <Popconfirm
                title="确定要删除这条应收记录吗?"
                onConfirm={() => handleDelete(record.id)}
                okText="确定"
                cancelText="取消"
              >
                <Button type="link" danger icon={<DeleteOutlined />}>
                  删除
                </Button>
              </Popconfirm>
            </>
          )}
        </Space>
      )
    }
  ];

  useEffect(() => {
    fetchCustomers();
    fetchStats();
  }, []);

  useEffect(() => {
    fetchReceivables();
  }, [pagination.current, pagination.pageSize, searchText, filters]);

  const fetchCustomers = async () => {
    try {
      const response = await customerAPI.getList({ limit: 1000 });
      setCustomers(response.data || []);
    } catch (error) {
      console.error('获取客户列表失败', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await receivableAPI.getStats();
      setStats(response);
    } catch (error) {
      console.error('获取统计失败', error);
    }
  };

  const fetchReceivables = async () => {
    try {
      setLoading(true);
      const response = await receivableAPI.getList({
        page: pagination.current,
        limit: pagination.pageSize,
        search: searchText,
        ...filters
      });

      setReceivables(response.data);
      setPagination(prev => ({
        ...prev,
        total: response.pagination.total
      }));
    } catch (error) {
      message.error('获取应收账款列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize
    }));
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleReset = () => {
    setSearchText('');
    setFilters({ status: '', customerId: '' });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleAdd = () => {
    setEditingReceivable(null);
    form.resetFields();
    form.setFieldsValue({
      billDate: dayjs(),
      status: 'pending'
    });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingReceivable(record);
    form.setFieldsValue({
      ...record,
      billDate: record.billDate ? dayjs(record.billDate) : null,
      dueDate: record.dueDate ? dayjs(record.dueDate) : null
    });
    setModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const data = {
        ...values,
        billDate: values.billDate?.format('YYYY-MM-DD'),
        dueDate: values.dueDate?.format('YYYY-MM-DD')
      };

      if (editingReceivable) {
        await receivableAPI.update(editingReceivable.id, data);
        message.success('更新成功');
      } else {
        await receivableAPI.create(data);
        message.success('创建成功');
      }

      setModalVisible(false);
      fetchReceivables();
      fetchStats();
    } catch (error) {
      if (error.errorFields) {
        message.error('请填写完整的表单');
      } else {
        message.error(error.response?.data?.message || '操作失败');
      }
    }
  };

  const handleDelete = async (id) => {
    try {
      await receivableAPI.delete(id);
      message.success('删除成功');
      fetchReceivables();
      fetchStats();
    } catch (error) {
      message.error(error.response?.data?.message || '删除失败');
    }
  };

  const handleReceive = (record) => {
    setCurrentReceivable(record);
    receiveForm.resetFields();
    receiveForm.setFieldsValue({
      amount: parseFloat(record.pendingAmount),
      paymentMethod: 'bank',
      paymentDate: dayjs()
    });
    setReceiveModalVisible(true);
  };

  const handleReceiveOk = async () => {
    try {
      const values = await receiveForm.validateFields();
      const data = {
        ...values,
        paymentDate: values.paymentDate?.format('YYYY-MM-DD')
      };

      await receivableAPI.receive(currentReceivable.id, data);
      message.success('收款成功');
      setReceiveModalVisible(false);
      fetchReceivables();
      fetchStats();
    } catch (error) {
      if (error.errorFields) {
        message.error('请填写完整的表单');
      } else {
        message.error(error.response?.data?.message || '收款失败');
      }
    }
  };

  const handleViewDetail = async (record) => {
    try {
      const response = await receivableAPI.getDetail(record.id);
      setReceivableDetail(response);
      setDetailModalVisible(true);
    } catch (error) {
      message.error('获取详情失败');
    }
  };

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>应收账款管理</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          新增应收
        </Button>
      </div>

      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={4}>
          <Card>
            <Statistic title="应收总额" value={stats.totalAmount || 0} prefix="¥" precision={2} />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic title="已收金额" value={stats.receivedAmount || 0} prefix="¥" precision={2} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic title="待收金额" value={stats.pendingAmount || 0} prefix="¥" precision={2} valueStyle={{ color: '#ff4d4f' }} />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic title="待收款单" value={stats.pendingCount || 0} suffix="笔" />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic title="部分收款" value={stats.partialCount || 0} suffix="笔" valueStyle={{ color: '#1890ff' }} />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic title="逾期金额" value={stats.overdueAmount || 0} prefix="¥" precision={2} valueStyle={{ color: '#ff4d4f' }} />
          </Card>
        </Col>
      </Row>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col>
            <Search
              placeholder="搜索单号"
              onSearch={handleSearch}
              style={{ width: 200 }}
              allowClear
            />
          </Col>
          <Col>
            <Select
              placeholder="选择客户"
              style={{ width: 150 }}
              allowClear
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
              value={filters.customerId || undefined}
              onChange={(value) => handleFilterChange('customerId', value)}
            >
              {customers.map(c => (
                <Option key={c.id} value={c.id}>{c.shortName || c.name}</Option>
              ))}
            </Select>
          </Col>
          <Col>
            <Select
              placeholder="状态"
              style={{ width: 120 }}
              allowClear
              value={filters.status || undefined}
              onChange={(value) => handleFilterChange('status', value)}
            >
              <Option value="pending">待收款</Option>
              <Option value="partial">部分收款</Option>
              <Option value="completed">已完成</Option>
              <Option value="overdue">已逾期</Option>
            </Select>
          </Col>
          <Col>
            <Button icon={<ReloadOutlined />} onClick={handleReset}>
              重置
            </Button>
          </Col>
        </Row>
      </Card>

      <Table
        columns={columns}
        dataSource={receivables}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条记录`
        }}
        onChange={handleTableChange}
        scroll={{ x: 1400 }}
      />

      {/* 新增/编辑弹窗 */}
      <Modal
        title={editingReceivable ? '编辑应收单' : '新增应收单'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        width={600}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="customerId"
                label="客户"
                rules={[{ required: true, message: '请选择客户' }]}
              >
                <Select
                  placeholder="请选择客户"
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {customers.map(c => (
                    <Option key={c.id} value={c.id}>{c.shortName || c.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="totalAmount"
                label="应收金额"
                rules={[{ required: true, message: '请输入应收金额' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0.01}
                  precision={2}
                  prefix="¥"
                  placeholder="请输入应收金额"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="billDate"
                label="账单日期"
                rules={[{ required: true, message: '请选择账单日期' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="dueDate" label="到期日期">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="relatedOrderType" label="关联单据类型">
                <Select placeholder="请选择单据类型" allowClear>
                  <Option value="sales_order">销售订单</Option>
                  <Option value="other">其他</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="relatedOrderNo" label="关联单号">
                <Input placeholder="请输入关联单号" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="remark" label="备注">
            <TextArea rows={2} placeholder="请输入备注" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 收款弹窗 */}
      <Modal
        title={`收款 - ${currentReceivable?.billNo}`}
        open={receiveModalVisible}
        onOk={handleReceiveOk}
        onCancel={() => setReceiveModalVisible(false)}
        width={500}
        destroyOnClose
      >
        {currentReceivable && (
          <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
            <Row gutter={16}>
              <Col span={12}>
                <span>应收金额：</span>
                <span style={{ fontWeight: 'bold' }}>¥{parseFloat(currentReceivable.totalAmount).toFixed(2)}</span>
              </Col>
              <Col span={12}>
                <span>待收金额：</span>
                <span style={{ fontWeight: 'bold', color: '#ff4d4f' }}>¥{parseFloat(currentReceivable.pendingAmount).toFixed(2)}</span>
              </Col>
            </Row>
          </div>
        )}
        <Form form={receiveForm} layout="vertical">
          <Form.Item
            name="amount"
            label="收款金额"
            rules={[{ required: true, message: '请输入收款金额' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0.01}
              max={currentReceivable ? parseFloat(currentReceivable.pendingAmount) : undefined}
              precision={2}
              prefix="¥"
              placeholder="请输入收款金额"
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="paymentMethod"
                label="收款方式"
                rules={[{ required: true, message: '请选择收款方式' }]}
              >
                <Select placeholder="请选择收款方式">
                  <Option value="cash">现金</Option>
                  <Option value="bank">银行转账</Option>
                  <Option value="wechat">微信</Option>
                  <Option value="alipay">支付宝</Option>
                  <Option value="check">支票</Option>
                  <Option value="other">其他</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="paymentDate"
                label="收款日期"
                rules={[{ required: true, message: '请选择收款日期' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="bankAccount" label="银行账号">
                <Input placeholder="请输入银行账号" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="transactionNo" label="交易流水号">
                <Input placeholder="请输入交易流水号" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="remark" label="备注">
            <TextArea rows={2} placeholder="请输入备注" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 详情弹窗 */}
      <Modal
        title={`应收单详情 - ${receivableDetail?.billNo}`}
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={700}
      >
        {receivableDetail && (
          <div>
            <Card title="基本信息" size="small" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <p><strong>应收单号：</strong>{receivableDetail.billNo}</p>
                  <p><strong>客户：</strong>{receivableDetail.customer?.name}</p>
                  <p><strong>账单日期：</strong>{dayjs(receivableDetail.billDate).format('YYYY-MM-DD')}</p>
                  <p><strong>到期日期：</strong>{receivableDetail.dueDate ? dayjs(receivableDetail.dueDate).format('YYYY-MM-DD') : '-'}</p>
                </Col>
                <Col span={12}>
                  <p><strong>应收金额：</strong><span style={{ color: '#1890ff' }}>¥{parseFloat(receivableDetail.totalAmount).toFixed(2)}</span></p>
                  <p><strong>已收金额：</strong><span style={{ color: '#52c41a' }}>¥{parseFloat(receivableDetail.receivedAmount).toFixed(2)}</span></p>
                  <p><strong>待收金额：</strong><span style={{ color: '#ff4d4f' }}>¥{parseFloat(receivableDetail.pendingAmount).toFixed(2)}</span></p>
                  <p><strong>状态：</strong><Tag color={statusMap[receivableDetail.status]?.color}>{statusMap[receivableDetail.status]?.text}</Tag></p>
                </Col>
              </Row>
              {receivableDetail.remark && <p><strong>备注：</strong>{receivableDetail.remark}</p>}
            </Card>

            {receivableDetail.payments && receivableDetail.payments.length > 0 && (
              <Card title="收款记录" size="small">
                <Table
                  dataSource={receivableDetail.payments}
                  rowKey="id"
                  size="small"
                  pagination={false}
                  columns={[
                    { title: '收款单号', dataIndex: 'paymentNo', width: 140 },
                    { title: '金额', dataIndex: 'amount', width: 100, render: (text) => `¥${parseFloat(text).toFixed(2)}` },
                    { title: '收款方式', dataIndex: 'paymentMethod', width: 100, render: (text) => paymentMethodMap[text] || text },
                    { title: '收款日期', dataIndex: 'paymentDate', width: 110, render: (text) => dayjs(text).format('YYYY-MM-DD') },
                    { title: '操作人', dataIndex: ['operator', 'realName'], width: 100 }
                  ]}
                />
              </Card>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ReceivableList;
